import { canvasRequest } from '$/api/canvasRquest';
import App from '$/api/api';
import { useLanguageStore } from '$/store/languageStore';
import type {
  CreateTeamParams,
  TeamInviteData,
  TeamInvitePreviewData,
  TeamJoinApplyData,
  TeamJoinApplyParams,
  TeamJoinRequestListData,
  TeamJoinReviewParams,
  TeamImportAssetsResult,
  TeamListItem,
  SaveTeamMemberCreditQuotaParams,
  TeamMemberListData,
  TeamMemberTargetParams,
  TeamSettings,
  UpdateTeamParams,
  UpdateTeamSettingsParams,
} from './types';

interface TeamMembersApiResponse {
  code: number;
  message?: string;
  data?: TeamMemberListData;
}

interface TeamInfoApiResponse {
  code: number;
  message?: string;
  data?: TeamListItem;
}

interface TeamJoinRequestsApiResponse {
  code: number;
  message?: string;
  data?: TeamJoinRequestListData;
}

interface TeamJoinReviewApiResponse {
  code: number;
  message?: string;
  data?: unknown;
}

interface TeamMemberCreditQuotaSaveApiResponse {
  code: number;
  message?: string;
  data?: Record<string, never>;
}

interface TeamInviteApiResponse<T> {
  code: number;
  message?: string;
  data?: T;
}

interface TeamSettingsApiResponse {
  code: number;
  message?: string;
  data?: TeamSettings;
}

interface TeamMutationApiResponse {
  code: number;
  message?: string;
  data?: unknown;
}

/**
 * 获取团队列表
 *
 * @export
 * @return {*}
 */
export async function getTeamList(): Promise<{
  list: TeamListItem[];
  current_oid: number;
  current_team: TeamListItem;
  current_identity: 'team' | 'personal';
}> {
  try {
    const response = await canvasRequest.request({
      baseURL: App.roboneo.baseCanvasURL(),
      url: App.roboneo.api.teamList,
      method: 'GET',
    });
    if (response.data.code === 0) {
      return response.data.data;
    }
    throw new Error(response.data);
  } catch (error) {
    console.error('Proxy Test Error:', error);
    throw error;
  }
}

/**
 * 创建团队
 *
 * @export
 * @param {CreateTeamParams} params - 创建团队参数
 * @return {Promise<TeamListItem>} 新建团队信息
 */
export async function createTeam(params: CreateTeamParams): Promise<TeamListItem> {
  try {
    const response = await canvasRequest.request({
      baseURL: App.roboneo.baseCanvasURL(),
      url: App.roboneo.api.teamCreate,
      method: 'POST',
      data: params,
    });
    if (response.data.code === 0) {
      return response.data.data;
    }
    throw response.data;
  } catch (error) {
    console.error('Create Team Error:', error);
    throw error;
  }
}

/**
 * 获取当前团队信息
 *
 * @export
 * @return {*}  {(Promise<{
 *   current_oid: number | 0; 0为个人账号，其他为团队账号
 *   team: TeamListItem;
 *   identity: 'team' | 'personal';
 * }>)}
 */
export async function getCurrentTeamInfo(): Promise<{
  current_oid: number | 0;
  team: TeamListItem;
  identity: 'team' | 'personal';
}> {
  try {
    const response = await canvasRequest.request({
      baseURL: App.roboneo.baseCanvasURL(),
      url: App.roboneo.api.teamCurrent,
      method: 'GET',
    });
    if (response.data.code === 0) {
      return response.data.data;
    }
    throw new Error(response.data);
  } catch (error) {
    console.error('Proxy Test Error:', error);
    throw error;
  }
}

/**
 * 获取指定团队的实时详情与商业快照。
 *
 * @param oid 团队 ID，同时显式通过 query 传递，避免首次水合时尚未注入团队请求头
 * @return {Promise<TeamListItem>} 团队版本、积分、席位和到期时间等实时详情
 */
export async function getTeamInfo(oid: number): Promise<TeamListItem> {
  try {
    const response = await canvasRequest.request({
      baseURL: App.roboneo.baseCanvasURL(),
      url: App.roboneo.api.teamInfo,
      method: 'GET',
      params: { oid },
    }) as { data: TeamInfoApiResponse; };

    if (response.data.code === 0 && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Get team info failed');
  } catch (error) {
    console.error('Get Team Info Error:', error);
    throw error;
  }
}

/**
 * 切换团队
 *
 * @export
 * @param {number | 0} oid - 团队 ID
 * @return {Promise<{ identity: 'team' | 'personal'; current_oid: number; }>} 切换团队结果
 */
export async function teamSwitch(oid: number | 0): Promise<{
  identity: 'team' | 'personal';
  current_oid: number;
}> {
  try {
    const response = await canvasRequest.request({
      baseURL: App.roboneo.baseCanvasURL(),
      url: App.roboneo.api.teamSwitch,
      method: 'POST',
      data: { oid },
    });
    if (response.data.code === 0) {
      return response.data.data;
    }
    throw new Error(response.data);
  } catch (error) {
    console.error('Switch Team Error:', error);
    throw error;
  }
}

/**
 * 更新当前团队的名称或头像，仅团队管理员可调用。
 *
 * @param params 至少包含名称或头像中的一个字段
 * @return {Promise<void>} 接口 code 为 0 时完成
 */
export async function updateTeam(params: UpdateTeamParams): Promise<void> {
  try {
    const response = await canvasRequest.request({
      baseURL: App.roboneo.baseCanvasURL(),
      url: App.roboneo.api.teamUpdate,
      method: 'POST',
      data: params,
    }) as { data: TeamMutationApiResponse; };

    if (response.data.code === 0) {
      return;
    }

    throw new Error(response.data.message || useLanguageStore.getState()
      .languageStore.smartcatTranslationText((v) => v.update_team_info_failed));
  } catch (error) {
    console.error('Update Team Error:', error);
    throw error;
  }
}

/**
 * 解散当前纯免费团队，服务端会再次校验商业态和积分余额。
 *
 * @return {Promise<void>} 接口 code 为 0 时完成
 */
export async function dissolveTeam(): Promise<void> {
  try {
    const response = await canvasRequest.request({
      baseURL: App.roboneo.baseCanvasURL(),
      url: App.roboneo.api.teamDissolve,
      method: 'POST',
      data: { confirm: 1 },
    }) as { data: TeamMutationApiResponse; };

    if (response.data.code === 0) {
      return;
    }

    throw new Error(response.data.message || useLanguageStore.getState()
      .languageStore.smartcatTranslationText((v) => v.dissolve_team_failed));
  } catch (error) {
    console.error('Dissolve Team Error:', error);
    throw error;
  }
}

/**
 * 当前普通成员退出团队；管理员需先完成管理员转让。
 *
 * @return {Promise<void>} 接口 code 为 0 时完成
 */
export async function leaveTeam(): Promise<void> {
  try {
    const response = await canvasRequest.request({
      baseURL: App.roboneo.baseCanvasURL(),
      url: App.roboneo.api.teamLeave,
      method: 'POST',
      data: {},
    }) as { data: TeamMutationApiResponse; };

    if (response.data.code === 0) {
      return;
    }

    throw new Error(response.data.message || useLanguageStore.getState()
      .languageStore.smartcatTranslationText((v) => v.leave_team_failed));
  } catch (error) {
    console.error('Leave Team Error:', error);
    throw error;
  }
}

/**
 * 获取当前团队成员列表，请求层会自动携带当前团队的 Client-OID。
 *
 * @export
 * @return {Promise<TeamMemberListData>} 当前团队成员数据
 */
export async function getTeamMembers(): Promise<TeamMemberListData> {
  try {
    const response = await canvasRequest.request({
      baseURL: App.roboneo.baseCanvasURL(),
      url: App.roboneo.api.teamMembers,
      method: 'GET',
    }) as { data: TeamMembersApiResponse; };

    if (response.data.code === 0 && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Get team members failed');
  } catch (error) {
    console.error('Get Team Members Error:', error);
    throw error;
  }
}

/**
 * 批量保存当前团队成员的本月积分额度，请求层会自动携带 Client-OID 和 client_id。
 *
 * @param params 非空成员额度数组，调用方需保证单批不超过 50 人
 * @return {Promise<void>} 保存成功后由调用方重新查询成员额度
 */
export async function saveTeamMemberCreditQuotas(
  params: SaveTeamMemberCreditQuotaParams,
): Promise<void> {
  try {
    const response = await canvasRequest.request({
      baseURL: App.roboneo.baseCanvasURL(),
      url: App.roboneo.api.teamMemberCreditQuotaSave,
      method: 'POST',
      data: params,
    }) as { data: TeamMemberCreditQuotaSaveApiResponse; };

    if (response.data.code === 0) {
      return;
    }

    throw new Error(response.data.message || useLanguageStore.getState()
      .languageStore.smartcatTranslationText(
        (v) => v.credit_quota_config_save_failed_retry_later,
      ));
  } catch (error) {
    console.error('Save Team Member Credit Quotas Error:', error);
    throw error;
  }
}

/**
 * 将普通成员移出当前团队，仅团队管理员可调用。
 *
 * @param params 被移出成员的 UID
 * @return {Promise<void>} 接口 code 为 0 时完成
 */
export async function removeTeamMember(
  params: TeamMemberTargetParams,
): Promise<void> {
  try {
    const response = await canvasRequest.request({
      baseURL: App.roboneo.baseCanvasURL(),
      url: App.roboneo.api.teamMemberRemove,
      method: 'POST',
      data: params,
    }) as { data: TeamMutationApiResponse; };

    if (response.data.code === 0) {
      return;
    }

    throw new Error(response.data.message || useLanguageStore.getState()
      .languageStore.smartcatTranslationText((v) => v.remove_team_member_failed));
  } catch (error) {
    console.error('Remove Team Member Error:', error);
    throw error;
  }
}

/**
 * 将管理员身份转让给指定普通成员，成功后当前管理员会降为普通成员。
 *
 * @param params 新管理员的 UID
 * @return {Promise<void>} 接口 code 为 0 时完成
 */
export async function transferTeamAdmin(
  params: TeamMemberTargetParams,
): Promise<void> {
  try {
    const response = await canvasRequest.request({
      baseURL: App.roboneo.baseCanvasURL(),
      url: App.roboneo.api.teamMemberTransferAdmin,
      method: 'POST',
      data: params,
    }) as { data: TeamMutationApiResponse; };

    if (response.data.code === 0) {
      return;
    }

    throw new Error(response.data.message || useLanguageStore.getState()
      .languageStore.smartcatTranslationText((v) => v.transfer_admin_failed));
  } catch (error) {
    console.error('Transfer Team Admin Error:', error);
    throw error;
  }
}

/**
 * 获取当前团队权限设置；普通成员也可读取但只能查看。
 *
 * @return {Promise<TeamSettings>} 当前团队权限设置
 */
export async function getTeamSettings(): Promise<TeamSettings> {
  try {
    const response = await canvasRequest.request({
      baseURL: App.roboneo.baseCanvasURL(),
      url: App.roboneo.api.teamSettings,
      method: 'GET',
    }) as { data: TeamSettingsApiResponse; };

    if (response.data.code === 0 && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.message || useLanguageStore.getState()
      .languageStore.smartcatTranslationText((v) => v.get_team_permission_settings_failed));
  } catch (error) {
    console.error('Get Team Settings Error:', error);
    throw error;
  }
}

/**
 * 更新当前团队发生变化的权限开关，仅团队管理员可调用。
 *
 * @param params 需要更新的权限开关
 * @return {Promise<void>} 接口 code 为 0 时完成
 */
export async function updateTeamSettings(
  params: UpdateTeamSettingsParams,
): Promise<void> {
  try {
    const response = await canvasRequest.request({
      baseURL: App.roboneo.baseCanvasURL(),
      url: App.roboneo.api.teamSettingsUpdate,
      method: 'POST',
      data: params,
    }) as { data: TeamMutationApiResponse; };

    if (response.data.code === 0) {
      return;
    }

    throw new Error(response.data.message || useLanguageStore.getState()
      .languageStore.smartcatTranslationText((v) => v.update_team_permission_settings_failed));
  } catch (error) {
    console.error('Update Team Settings Error:', error);
    throw error;
  }
}

/**
 * 获取或生成当前团队的永久邀请码，仅团队管理员可调用。
 *
 * @return {Promise<TeamInviteData>} 当前团队的邀请码
 */
export async function getTeamInvite(): Promise<TeamInviteData> {
  try {
    const response = await canvasRequest.request({
      baseURL: App.roboneo.baseCanvasURL(),
      url: App.roboneo.api.teamInvite,
      method: 'GET',
    }) as { data: TeamInviteApiResponse<TeamInviteData>; };

    if (response.data.code === 0 && response.data.data?.invite_code) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Get team invite failed');
  } catch (error) {
    console.error('Get Team Invite Error:', error);
    throw error;
  }
}

/**
 * 登录后凭邀请码预览目标团队，不依赖当前团队身份。
 *
 * @param inviteCode 永久邀请码
 * @return {Promise<TeamInvitePreviewData>} 邀请团队预览信息
 */
export async function getTeamInvitePreview(
  inviteCode: string,
): Promise<TeamInvitePreviewData> {
  try {
    const response = await canvasRequest.request({
      baseURL: App.roboneo.baseCanvasURL(),
      url: App.roboneo.api.teamInvitePeek,
      method: 'GET',
      params: { invite_code: inviteCode },
    }) as { data: TeamInviteApiResponse<TeamInvitePreviewData>; };

    if (response.data.code === 0 && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Get team invite preview failed');
  } catch (error) {
    console.error('Get Team Invite Preview Error:', error);
    throw error;
  }
}

/**
 * 登录用户凭永久邀请码申请加入团队，重复申请由服务端幂等返回原申请。
 *
 * @param params 加入团队申请参数
 * @return {Promise<TeamJoinApplyData>} 加入申请结果
 */
export async function applyToJoinTeam(
  params: TeamJoinApplyParams,
): Promise<TeamJoinApplyData> {
  try {
    const response = await canvasRequest.request({
      baseURL: App.roboneo.baseCanvasURL(),
      url: App.roboneo.api.teamJoinApply,
      method: 'POST',
      data: params,
    }) as { data: TeamInviteApiResponse<TeamJoinApplyData>; };

    if (response.data.code === 0 && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Apply to join team failed');
  } catch (error) {
    console.error('Apply To Join Team Error:', error);
    throw error;
  }
}

/**
 * 获取当前团队的加入申请审核消息，仅管理员可调用。
 *
 * @param oid 当前团队 ID，GET 请求通过 query 显式传递，公共请求层同时会携带 Client-OID
 * @return {Promise<TeamJoinRequestListData>} 最近的加入申请审核消息
 */
export async function getTeamJoinRequests(
  oid: number,
): Promise<TeamJoinRequestListData> {
  try {
    const response = await canvasRequest.request({
      baseURL: App.roboneo.baseCanvasURL(),
      url: App.roboneo.api.teamJoinRequests,
      method: 'GET',
      params: { oid },
    }) as { data: TeamJoinRequestsApiResponse; };

    if (response.data.code === 0 && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Get team join requests failed');
  } catch (error) {
    console.error('Get Team Join Requests Error:', error);
    throw error;
  }
}

/**
 * 审核当前团队的加入申请，仅管理员可调用。
 *
 * @param oid 当前团队 ID，公共请求层同时会携带 Client-OID
 * @param params 审核请求参数，approve 为 1 时通过、为 0 时拒绝
 * @return {Promise<void>} 接口 code 为 0 时完成
 */
export async function reviewTeamJoinRequest(
  oid: number,
  params: TeamJoinReviewParams,
): Promise<void> {
  try {
    const response = await canvasRequest.request({
      baseURL: App.roboneo.baseCanvasURL(),
      url: App.roboneo.api.teamJoinReview,
      method: 'POST',
      params: { oid },
      data: params,
    }) as { data: TeamJoinReviewApiResponse; };

    if (response.data.code === 0) {
      return;
    }

    // 保留业务码，审核页才能在 15210/15211 竞态失败后继续锁定该申请的通过入口。
    const reviewError = new Error(
      response.data.message || useLanguageStore.getState()
        .languageStore.smartcatTranslationText(
          (v) => v.review_join_request_failed_retry_later,
        ),
    ) as Error & { code: number; };
    reviewError.code = response.data.code;
    throw reviewError;
  } catch (error) {
    console.error('Review Team Join Request Error:', error);
    throw error;
  }
}

/**
 * 将个人资产导入当前团队的指定文件夹；请求层保留当前团队 Client-OID，资产 ID 使用字符串避免 int64 精度损失。
 *
 * @param assetIds 待导入的个人资产 ID
 * @param folderId 团队资产库目标文件夹 ID
 * @return {Promise<TeamImportAssetsResult>} 成功及覆盖数量
 */
export async function importPersonalAssetsToTeam(
  assetIds: Array<number | string>,
  folderId: number,
): Promise<TeamImportAssetsResult> {
  try {
    const response = await canvasRequest.request({
      baseURL: App.roboneo.baseCanvasURL(),
      url: App.roboneo.api.teamImportAssets,
      method: 'POST',
      data: {
        asset_ids: assetIds.map(String),
        folder_id: folderId,
      },
    }) as { data: TeamInviteApiResponse<TeamImportAssetsResult>; };

    if (response.data.code === 0 && response.data.data) {
      return {
        count: Number(response.data.data.count) || 0,
        overwrite: Number(response.data.data.overwrite) || 0,
      };
    }

    throw new Error(
      response.data.message || 'failed_to_import_from_personal_assets_please_try_again_later_',
    );
  } catch (error) {
    console.error('Import Personal Assets To Team Error:', error);
    throw error;
  }
}
